import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Shield, ChevronRight, LogOut, UserCircle, LayoutDashboard, Building2, Wrench, Users, Gift, BarChart3, FileClock, Wallet } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import ThemeToggle from "@/components/ThemeToggle";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const linksMeta = [
  { to: "/file-claim", key: "nav.file" },
  { to: "/track", key: "nav.track" },
  { to: "/enterprise", key: "nav.enterprise" },
  { to: "/emergency", key: "nav.emergency" },
  { to: "/knowledge", key: "nav.knowledge" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const links = linksMeta.map(l => ({ ...l, label: t(l.key) }));

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 dark:bg-slate-950/70 border-b border-slate-200 dark:border-slate-800"
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
          <div className="w-10 h-10 rounded-xl overflow-hidden shadow-lg shadow-blue-700/20 group-hover:scale-105 transition-transform">
            <img src="/brand/logo.webp" alt="myinsuranceclaim.in" className="w-full h-full object-cover" />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-extrabold text-[17px] text-slate-900 dark:text-white tracking-tight">
              myinsuranceclaim<span className="text-orange-500">.in</span>
            </div>
            <div className="text-[10px] text-slate-500 dark:text-slate-400 font-medium tracking-wide uppercase">{t("header.tagline")}</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="px-3 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 hover:text-blue-800 dark:hover:text-blue-400 hover:bg-slate-50 dark:hover:bg-slate-800/50 rounded-lg transition-colors"
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2">
          <LanguageSwitcher />
          <ThemeToggle className="theme-toggle-desktop" />
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700 hover:border-blue-700 dark:hover:border-blue-400 transition-colors"
                  data-testid="user-menu-button"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
                  ) : (
                    <UserCircle className="w-7 h-7 text-slate-400" />
                  )}
                  <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">{user.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold">{user.name}</div>
                  <div className="text-xs text-slate-500">{user.email}</div>
                  <div className="text-[10px] mt-1 inline-block px-2 py-0.5 bg-blue-100 dark:bg-blue-950/40 text-blue-900 dark:text-blue-300 font-bold rounded uppercase tracking-wider">
                    {user.role}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                  <LayoutDashboard className="w-4 h-4 mr-2" />
                  Dashboard
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/rewards")} data-testid="menu-rewards">
                  <Gift className="w-4 h-4 mr-2" />
                  Rewards
                </DropdownMenuItem>
                {["admin", "super_admin"].includes(user.role) && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <Shield className="w-4 h-4 mr-2" />
                    Admin Panel
                  </DropdownMenuItem>
                )}
                {["admin", "super_admin"].includes(user.role) && (
                  <DropdownMenuItem onClick={() => navigate("/admin/analytics")} data-testid="menu-admin-analytics">
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Analytics
                  </DropdownMenuItem>
                )}
                {["admin", "super_admin"].includes(user.role) && (
                  <DropdownMenuItem onClick={() => navigate("/admin/war-room")} data-testid="menu-war-room">
                    <Shield className="w-4 h-4 mr-2" />
                    War Room
                  </DropdownMenuItem>
                )}
                {["admin", "super_admin", "cfo", "finance"].includes(user.role) && (
                  <DropdownMenuItem onClick={() => navigate("/admin/finance")} data-testid="menu-finance">
                    <Wallet className="w-4 h-4 mr-2" />
                    Finance
                  </DropdownMenuItem>
                )}
                {["admin", "super_admin", "coo", "branch_manager", "finance", "cfo", "compliance", "auditor", "claim_manager"].includes(user.role) && (
                  <DropdownMenuItem onClick={() => navigate("/admin/workshops")} data-testid="menu-workshops-admin">
                    <Building2 className="w-4 h-4 mr-2" />
                    Workshops
                  </DropdownMenuItem>
                )}
                {["admin", "super_admin", "auditor", "compliance"].includes(user.role) && (
                  <DropdownMenuItem onClick={() => navigate("/admin/audit")} data-testid="menu-audit">
                    <FileClock className="w-4 h-4 mr-2" />
                    Audit Trail
                  </DropdownMenuItem>
                )}
                {["workshop", "admin", "super_admin"].includes(user.role) && (
                  <DropdownMenuItem onClick={() => navigate("/workshop")} data-testid="menu-workshop">
                    <Wrench className="w-4 h-4 mr-2" />
                    Workshop Portal
                  </DropdownMenuItem>
                )}
                {["claim_manager", "admin", "super_admin"].includes(user.role) && (
                  <DropdownMenuItem onClick={() => navigate("/claim-manager")} data-testid="menu-claim-manager">
                    <Users className="w-4 h-4 mr-2" />
                    Claim Manager
                  </DropdownMenuItem>
                )}
                {["partner", "surveyor"].includes(user.role) && (
                  <DropdownMenuItem onClick={() => navigate("/partner-dashboard")} data-testid="menu-partner">
                    <Building2 className="w-4 h-4 mr-2" />
                    Partner Console
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button
                variant="ghost"
                className="text-slate-700 dark:text-slate-200"
                onClick={handleLogin}
                data-testid="header-login-button"
              >
                {t("cta.signIn")}
              </Button>
              <button
                onClick={() => navigate("/file-claim")}
                className="btn-signature rounded-lg px-5 py-2 text-sm font-semibold flex items-center gap-1"
                data-testid="header-file-claim-button"
              >
                {t("cta.fileClaim")} <ChevronRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>

        <div className="lg:hidden flex items-center gap-2">
          <LanguageSwitcher />
          <ThemeToggle className="theme-toggle-mobile" />
          <button
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
            onClick={() => setOpen((s) => !s)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="text-slate-700 dark:text-slate-200" /> : <Menu className="text-slate-700 dark:text-slate-200" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950" data-testid="mobile-menu">
          <div className="px-4 py-3 flex flex-col gap-1">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="px-3 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-lg"
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {l.label}
              </Link>
            ))}
            <div className="border-t border-slate-100 dark:border-slate-800 mt-2 pt-2 flex flex-col gap-2">
              {user ? (
                <>
                  <Link to="/dashboard" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold text-blue-800 dark:text-blue-400">Dashboard</Link>
                  {["admin", "super_admin"].includes(user.role) && <Link to="/admin" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold text-blue-800 dark:text-blue-400">Admin</Link>}
                  {["admin", "super_admin", "cfo", "finance"].includes(user.role) && <Link to="/admin/finance" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold text-emerald-700 dark:text-emerald-400">Finance</Link>}
                  {["admin", "super_admin", "auditor", "compliance"].includes(user.role) && <Link to="/admin/audit" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold text-orange-700 dark:text-orange-400">Audit Trail</Link>}
                  {["workshop", "admin"].includes(user.role) && <Link to="/workshop" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-semibold text-blue-800 dark:text-blue-400">Workshop Portal</Link>}
                  <button onClick={handleLogout} className="px-3 py-2 text-sm font-semibold text-left text-slate-700 dark:text-slate-300">Sign out</button>
                </>
              ) : (
                <button onClick={handleLogin} className="btn-signature rounded-lg px-4 py-2 text-sm font-semibold">Sign in with Google</button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
